import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  X, Search, BookmarkPlus, LayoutList, Loader2, Trash2, Folder, FolderOpen, Plus, Pencil, Check,
} from 'lucide-react';
import { API_BASE } from '../config/api';

const FOLDER_ALL = '__all__';
const FOLDER_NONE = '__none__';

function sectionLibraryLayoutQuery(layoutId) {
  const id = layoutId != null && String(layoutId).trim() !== '' ? String(layoutId).trim() : null;
  return id ? `?layout_id=${encodeURIComponent(id)}` : '';
}

function withSectionLibraryLayout(layoutId, payload = {}) {
  const id = layoutId != null && String(layoutId).trim() !== '' ? String(layoutId).trim() : null;
  return id ? { ...payload, layout_id: id } : payload;
}

function formatSectionDate(iso, locale) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(locale, { dateStyle: 'medium' });
}

function folderLabelById(folderId, folders, t, unfiledLabel) {
  if (!folderId) {
    return unfiledLabel || t('sectionPresetNoFolder', { defaultValue: 'Sem pasta' });
  }
  const f = folders.find((x) => x.id === folderId);
  return f?.name || unfiledLabel || t('sectionPresetNoFolder', { defaultValue: 'Sem pasta' });
}

async function readApiErrorMessage(r, t, fallback) {
  const text = await r.text();
  try {
    const d = text ? JSON.parse(text) : {};
    if (d?.error) return String(d.error);
  } catch {
    /* resposta não-JSON */
  }
  if (r.status === 404) {
    return t('sectionPresetFolderBackend404', {
      defaultValue:
        'Recurso de pastas não encontrado na API. Atualize o backend (server.js + database.js), execute node migrate-section-presets.js e reinicie o PM2.',
    });
  }
  if (r.status === 403) {
    return t('sectionPresetTenantRequired', {
      defaultValue: 'Biblioteca de seções disponível para contas com tenant (mesma regra da galeria de templates).',
    });
  }
  if (text && text.length < 280) return text;
  return fallback;
}

function parseSectionTags(raw) {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) return raw.map((s) => String(s).trim()).filter(Boolean);
  try {
    const j = JSON.parse(raw);
    if (Array.isArray(j)) return j.map((s) => String(s).trim()).filter(Boolean);
  } catch {
    /* texto */
  }
  return String(raw).split(/[,;]+/).map((s) => s.trim()).filter(Boolean);
}

export default function SectionsLibraryModal({
  open,
  mode = 'insert',
  targetRowId = null,
  layoutId = null,
  authToken,
  onClose,
  onSave,
  onInsert,
  canDelete = false,
  canManageFolders = true,
  onDelete,
  canCurateAiLibrary = false,
}) {
  const { t, i18n } = useTranslation('builder');
  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'pt-BR';

  const [sections, setSections] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [activeFolderId, setActiveFolderId] = useState(FOLDER_ALL);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [overwriteId, setOverwriteId] = useState('');
  const [saveFolderId, setSaveFolderId] = useState('');
  const [saving, setSaving] = useState(false);
  const [insertingId, setInsertingId] = useState(null);

  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editingFolderName, setEditingFolderName] = useState('');
  const [folderBusyId, setFolderBusyId] = useState(null);
  const [unfiledLabel, setUnfiledLabel] = useState('');
  const [canWriteLibrary, setCanWriteLibrary] = useState(true);
  const [editingUnfiledLabel, setEditingUnfiledLabel] = useState(false);
  const [editingUnfiledName, setEditingUnfiledName] = useState('');

  /* Destino / aba: equipe (padrão) ou Global IA — só curador vê a opção */
  const [libraryScope, setLibraryScope] = useState('tenant'); /* tenant | ai_global */
  const [saveTarget, setSaveTarget] = useState('tenant');
  const [aiSectionRole, setAiSectionRole] = useState('content');
  const [aiTags, setAiTags] = useState('');
  const [aiTierMin, setAiTierMin] = useState('basic');
  const [aiMood, setAiMood] = useState('');
  const [aiStatus, setAiStatus] = useState('ready');
  const [editingAiId, setEditingAiId] = useState(null);
  const [aiMetaSaving, setAiMetaSaving] = useState(false);
  const isAiLibrary = canCurateAiLibrary && libraryScope === 'ai_global';
  const saveToAi = canCurateAiLibrary && (saveTarget === 'ai_global' || isAiLibrary);
  const [unfiledBusy, setUnfiledBusy] = useState(false);

  const defaultUnfiledLabel = t('sectionPresetNoFolder', { defaultValue: 'Sem pasta' });
  const noFolderLabel = unfiledLabel?.trim() || defaultUnfiledLabel;

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' }),
    [authToken]
  );

  const switchLibraryScope = (scope) => {
    setLibraryScope(scope);
    setActiveFolderId(FOLDER_ALL);
    setOverwriteId('');
    setEditingAiId(null);
    if (scope === 'ai_global') setSaveTarget('ai_global');
    else setSaveTarget('tenant');
  };

  const loadLibrary = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    setLoadError(null);
    try {
      if (canCurateAiLibrary && libraryScope === 'ai_global') {
        const r = await fetch(`${API_BASE}/section-presets/ai-global`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
        const nextSections = Array.isArray(d.sections) ? d.sections : [];
        setSections(nextSections);
        setFolders([]);
        setUnfiledLabel('');
        setCanWriteLibrary(true);
        return { sections: nextSections, folders: [] };
      }
      const r = await fetch(`${API_BASE}/section-presets${sectionLibraryLayoutQuery(layoutId)}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      const nextSections = Array.isArray(d.sections)
        ? d.sections
        : (Array.isArray(d) ? d : []);
      const nextFolders = Array.isArray(d.folders) ? d.folders : [];
      setSections(nextSections);
      setFolders(nextFolders);
      setUnfiledLabel(typeof d.unfiled_label === 'string' ? d.unfiled_label : '');
      setCanWriteLibrary(d.can_write !== false);
      return { sections: nextSections, folders: nextFolders };
    } catch (e) {
      setSections([]);
      setFolders([]);
      setLoadError(e.message || 'error');
      return { sections: [], folders: [] };
    } finally {
      setLoading(false);
    }
  }, [authToken, layoutId, libraryScope, canCurateAiLibrary]);

  const upsertFolderInState = useCallback((folder) => {
    if (!folder?.id) return;
    setFolders((prev) => {
      const idx = prev.findIndex((f) => f.id === folder.id);
      const entry = {
        id: folder.id,
        name: folder.name || '',
        section_count: folder.section_count ?? 0,
        sort_order: folder.sort_order ?? 0,
      };
      if (idx === -1) return [...prev, entry];
      const next = [...prev];
      next[idx] = { ...next[idx], ...entry };
      return next;
    });
  }, []);

  useEffect(() => {
    const tm = setTimeout(() => setQDebounced(q.trim().toLowerCase()), 280);
    return () => clearTimeout(tm);
  }, [q]);

  useEffect(() => {
    if (!open) {
      setQ('');
      setQDebounced('');
      setName('');
      setDescription('');
      setOverwriteId('');
      setSaveFolderId('');
      setLoadError(null);
      setInsertingId(null);
      setActiveFolderId(FOLDER_ALL);
      setNewFolderName('');
      setShowNewFolderInput(false);
      setEditingFolderId(null);
      setLibraryScope('tenant');
      setSaveTarget('tenant');
      setEditingAiId(null);
      return;
    }
    setEditingUnfiledLabel(false);
    setEditingUnfiledName('');
    loadLibrary();
  }, [open, loadLibrary, layoutId]);

  useEffect(() => {
    if (!open) return;
    loadLibrary();
  }, [libraryScope]); // eslint-disable-line react-hooks/exhaustive-deps — recarrega ao trocar aba IA/equipe

  useEffect(() => {
    if (!open || mode !== 'save') return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, mode, onClose]);

  const folderCounts = useMemo(() => {
    const byFolder = {};
    let none = 0;
    sections.forEach((s) => {
      if (!s.folder_id) {
        none += 1;
        return;
      }
      byFolder[s.folder_id] = (byFolder[s.folder_id] || 0) + 1;
    });
    return { byFolder, none, all: sections.length };
  }, [sections]);

  const sectionsInActiveFolder = useMemo(() => {
    if (activeFolderId === FOLDER_ALL) return sections;
    if (activeFolderId === FOLDER_NONE) return sections.filter((s) => !s.folder_id);
    return sections.filter((s) => s.folder_id === activeFolderId);
  }, [sections, activeFolderId]);

  const filtered = useMemo(() => {
    const base = isAiLibrary ? sections : sectionsInActiveFolder;
    if (!qDebounced) return base;
    return base.filter((s) => {
      const n = String(s.name ?? '').toLowerCase();
      const desc = String(s.description ?? '').toLowerCase();
      const author = String(s.author_name ?? '').toLowerCase();
      const role = String(s.section_role ?? '').toLowerCase();
      const tags = parseSectionTags(s.tags).join(' ').toLowerCase();
      const folderName = isAiLibrary
        ? ''
        : folderLabelById(s.folder_id, folders, t, noFolderLabel).toLowerCase();
      return (
        n.includes(qDebounced)
        || desc.includes(qDebounced)
        || author.includes(qDebounced)
        || folderName.includes(qDebounced)
        || role.includes(qDebounced)
        || tags.includes(qDebounced)
      );
    });
  }, [isAiLibrary, sections, sectionsInActiveFolder, qDebounced, folders, t, noFolderLabel]);

  const overwriteOptions = useMemo(() => {
    if (isAiLibrary || saveToAi) return sections;
    if (saveFolderId) return sections.filter((s) => s.folder_id === saveFolderId);
    return sections.filter((s) => !s.folder_id);
  }, [sections, saveFolderId, isAiLibrary, saveToAi]);

  const handleOverwriteChange = (id) => {
    setOverwriteId(id);
    if (!id) return;
    const found = sections.find((s) => s.id === id);
    if (found) {
      setName(found.name || '');
      setDescription(found.description || '');
      setSaveFolderId(found.folder_id || '');
      if (isAiLibrary || saveToAi) {
        setAiSectionRole(found.section_role || 'content');
        setAiTags(parseSectionTags(found.tags).join(', '));
        setAiTierMin(found.tier_min || 'basic');
        setAiMood(found.mood || '');
        setAiStatus(found.status || 'ready');
      }
    }
  };

  const openAiMetaEditor = (section, e) => {
    e?.stopPropagation?.();
    if (!section?.id) return;
    setEditingAiId(section.id);
    setName(section.name || '');
    setDescription(section.description || '');
    setAiSectionRole(section.section_role || 'content');
    setAiTags(parseSectionTags(section.tags).join(', '));
    setAiTierMin(section.tier_min || 'basic');
    setAiMood(section.mood || '');
    setAiStatus(section.status || 'ready');
  };

  const saveAiMeta = async () => {
    if (!editingAiId || !authToken) return;
    const trimmed = name.trim();
    if (!trimmed) {
      alert(t('sectionPresetNameRequired', { defaultValue: 'Informe o nome da seção.' }));
      return;
    }
    setAiMetaSaving(true);
    try {
      const r = await fetch(`${API_BASE}/section-presets/ai-global/${editingAiId}`, {
        method: 'PATCH',
        headers: authHeaders,
        body: JSON.stringify({
          name: trimmed,
          description: description.trim(),
          section_role: aiSectionRole,
          tags: aiTags,
          tier_min: aiTierMin,
          mood: aiMood,
          status: aiStatus,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Erro');
      setEditingAiId(null);
      await loadLibrary();
    } catch (err) {
      alert(err.message || t('sectionPresetSaveError', { defaultValue: 'Erro ao salvar seção.' }));
    } finally {
      setAiMetaSaving(false);
    }
  };

  const handleCreateFolder = async () => {
    const trimmed = newFolderName.trim();
    if (!trimmed) return;
    setCreatingFolder(true);
    try {
      const r = await fetch(`${API_BASE}/section-presets/folders`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(withSectionLibraryLayout(layoutId, { name: trimmed })),
      });
      if (!r.ok) {
        throw new Error(
          await readApiErrorMessage(
            r,
            t,
            t('sectionPresetFolderCreateError', { defaultValue: 'Erro ao criar pasta.' })
          )
        );
      }
      const d = await r.json().catch(() => ({}));
      const createdFolder = d.id
        ? { id: d.id, name: d.name || trimmed, section_count: 0, sort_order: 0 }
        : null;
      setNewFolderName('');
      setShowNewFolderInput(false);
      if (createdFolder) upsertFolderInState(createdFolder);
      const loaded = await loadLibrary();
      if (createdFolder && !(loaded?.folders || []).some((f) => f.id === createdFolder.id)) {
        upsertFolderInState(createdFolder);
      }
      if (createdFolder?.id) {
        setActiveFolderId(createdFolder.id);
        if (mode === 'save') setSaveFolderId(createdFolder.id);
      }
    } catch (e) {
      alert(e.message || t('sectionPresetFolderCreateError', { defaultValue: 'Erro ao criar pasta.' }));
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleRenameUnfiledLabel = async () => {
    const trimmed = editingUnfiledName.trim();
    if (!trimmed) return;
    setUnfiledBusy(true);
    try {
      const r = await fetch(`${API_BASE}/section-presets/library-settings`, {
        method: 'PATCH',
        headers: authHeaders,
        body: JSON.stringify(withSectionLibraryLayout(layoutId, { unfiled_label: trimmed })),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Erro');
      setUnfiledLabel(d.unfiled_label || trimmed);
      setEditingUnfiledLabel(false);
      setEditingUnfiledName('');
    } catch (e) {
      alert(e.message || t('sectionPresetUnfiledRenameError', { defaultValue: 'Erro ao renomear pasta padrão.' }));
    } finally {
      setUnfiledBusy(false);
    }
  };

  const handleRenameFolder = async (folderId) => {
    const trimmed = editingFolderName.trim();
    if (!trimmed || !folderId) return;
    setFolderBusyId(folderId);
    try {
      const r = await fetch(`${API_BASE}/section-presets/folders/${folderId}`, {
        method: 'PATCH',
        headers: authHeaders,
        body: JSON.stringify(withSectionLibraryLayout(layoutId, { name: trimmed })),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Erro');
      setEditingFolderId(null);
      setEditingFolderName('');
      await loadLibrary();
    } catch (e) {
      alert(e.message || t('sectionPresetFolderRenameError', { defaultValue: 'Erro ao renomear pasta.' }));
    } finally {
      setFolderBusyId(null);
    }
  };

  const handleDeleteFolder = async (folder) => {
    if (!folder?.id) return;
    const ok = confirm(
      t('sectionPresetFolderDeleteConfirm', {
        defaultValue: 'Excluir a pasta "{{name}}"? As seções dentro dela ficarão em "Sem pasta".',
        name: folder.name || '',
      })
    );
    if (!ok) return;
    setFolderBusyId(folder.id);
    try {
      const r = await fetch(`${API_BASE}/section-presets/folders/${folder.id}${sectionLibraryLayoutQuery(layoutId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Erro');
      if (activeFolderId === folder.id) setActiveFolderId(FOLDER_ALL);
      if (saveFolderId === folder.id) setSaveFolderId('');
      await loadLibrary();
    } catch (e) {
      alert(e.message || t('sectionPresetFolderDeleteError', { defaultValue: 'Erro ao excluir pasta.' }));
    } finally {
      setFolderBusyId(null);
    }
  };

  const handleMoveSection = async (section, folderId) => {
    if (!section?.id) return;
    const nextFolder = folderId || null;
    if ((section.folder_id || null) === nextFolder) return;
    try {
      const r = await fetch(`${API_BASE}/section-presets/${section.id}`, {
        method: 'PATCH',
        headers: authHeaders,
        body: JSON.stringify(withSectionLibraryLayout(layoutId, { folder_id: nextFolder })),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Erro');
      await loadLibrary();
    } catch (e) {
      alert(e.message || t('sectionPresetMoveError', { defaultValue: 'Erro ao mover seção.' }));
    }
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      alert(t('sectionPresetNameRequired', { defaultValue: 'Informe o nome da seção.' }));
      return;
    }
    if (!onSave) return;
    setSaving(true);
    try {
      if (saveToAi) {
        await onSave({
          name: trimmed,
          description: description.trim(),
          saveTarget: 'ai_global',
          overwriteId: overwriteId || null,
          sectionRole: aiSectionRole,
          tags: aiTags,
          tierMin: aiTierMin,
          mood: aiMood,
          status: aiStatus,
        });
      } else {
        await onSave({
          name: trimmed,
          description: description.trim(),
          overwriteId: overwriteId || null,
          folderId: saveFolderId || null,
          saveTarget: 'tenant',
        });
      }
      onClose?.();
    } catch (e) {
      alert(e.message || t('sectionPresetSaveError', { defaultValue: 'Erro ao salvar seção.' }));
    } finally {
      setSaving(false);
    }
  };

  const handleInsert = async (section) => {
    if (!section?.id || !targetRowId) return;
    setInsertingId(section.id);
    try {
      onInsert?.(section);
      onClose?.();
    } finally {
      setInsertingId(null);
    }
  };

  const handleDelete = async (section, e) => {
    e.stopPropagation();
    if (!section?.id) return;
    const ok = confirm(
      isAiLibrary
        ? t('sectionPresetAiDeleteConfirm', {
            defaultValue: 'Excluir a seção "{{name}}" da biblioteca IA (Global)?',
            name: section.name || '',
          })
        : t('sectionPresetDeleteConfirm', {
            defaultValue: 'Excluir a seção "{{name}}" da biblioteca da equipe?',
            name: section.name || '',
          })
    );
    if (!ok) return;
    try {
      const url = isAiLibrary
        ? `${API_BASE}/section-presets/ai-global/${section.id}`
        : `${API_BASE}/section-presets/${section.id}${sectionLibraryLayoutQuery(layoutId)}`;
      const r = await fetch(url, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Erro');
      if (editingAiId === section.id) setEditingAiId(null);
      await loadLibrary();
      onDelete?.(section.id);
    } catch (err) {
      alert(err.message || t('sectionPresetDeleteError', { defaultValue: 'Erro ao excluir seção.' }));
    }
  };

  const renderFolderSidebar = () => (
    <aside className="w-[168px] shrink-0 border-r border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800/40 flex flex-col min-h-0">
      <div className="px-2 py-2 text-[10px] font-bold uppercase tracking-wide text-slate-400">
        {t('sectionPresetFoldersTitle', { defaultValue: 'Pastas' })}
      </div>
      <div className="flex-1 overflow-y-auto px-1.5 space-y-0.5">
        <button
          type="button"
          onClick={() => setActiveFolderId(FOLDER_ALL)}
          className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-left text-xs font-medium transition-colors ${
            activeFolderId === FOLDER_ALL
              ? 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200'
              : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
          }`}
        >
          <LayoutList size={14} className="shrink-0" />
          <span className="truncate flex-1">{t('sectionPresetFolderAll', { defaultValue: 'Todas' })}</span>
          <span className="text-[10px] opacity-70">{folderCounts.all}</span>
        </button>
        <div className="group relative">
          {editingUnfiledLabel ? (
            <div className="flex items-center gap-1 px-1 py-1">
              <input
                value={editingUnfiledName}
                onChange={(e) => setEditingUnfiledName(e.target.value)}
                className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRenameUnfiledLabel();
                  if (e.key === 'Escape') { setEditingUnfiledLabel(false); setEditingUnfiledName(''); }
                }}
              />
              <button
                type="button"
                className="p-1 text-emerald-600"
                disabled={unfiledBusy}
                onClick={handleRenameUnfiledLabel}
              >
                <Check size={12} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setActiveFolderId(FOLDER_NONE);
                if (mode === 'save') setSaveFolderId('');
              }}
              className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-left text-xs font-medium transition-colors ${
                canManageFolders && canWriteLibrary ? 'pr-8' : ''
              } ${
                activeFolderId === FOLDER_NONE
                  ? 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200'
                  : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              <Folder size={14} className="shrink-0" />
              <span className="truncate flex-1">{noFolderLabel}</span>
              <span className="text-[10px] opacity-70">{folderCounts.none}</span>
            </button>
          )}
          {canManageFolders && canWriteLibrary && !editingUnfiledLabel && (
            <div className="absolute right-1 top-1/2 -translate-y-1/2 hidden group-hover:flex">
              <button
                type="button"
                className="p-0.5 rounded text-slate-400 hover:text-blue-600"
                title={t('sectionPresetUnfiledRename', { defaultValue: 'Renomear pasta padrão' })}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingUnfiledLabel(true);
                  setEditingUnfiledName(noFolderLabel);
                }}
              >
                <Pencil size={11} />
              </button>
            </div>
          )}
        </div>
        {folders.map((folder) => {
          const isActive = activeFolderId === folder.id;
          const isEditing = editingFolderId === folder.id;
          const count = folderCounts.byFolder[folder.id] || folder.section_count || 0;
          return (
            <div key={folder.id} className="group relative">
              {isEditing ? (
                <div className="flex items-center gap-1 px-1 py-1">
                  <input
                    value={editingFolderName}
                    onChange={(e) => setEditingFolderName(e.target.value)}
                    className="flex-1 min-w-0 px-1.5 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleRenameFolder(folder.id);
                      if (e.key === 'Escape') { setEditingFolderId(null); setEditingFolderName(''); }
                    }}
                  />
                  <button
                    type="button"
                    className="p-1 text-emerald-600"
                    disabled={folderBusyId === folder.id}
                    onClick={() => handleRenameFolder(folder.id)}
                  >
                    <Check size={12} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setActiveFolderId(folder.id);
                    if (mode === 'save') setSaveFolderId(folder.id);
                  }}
                  className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-left text-xs font-medium transition-colors pr-12 ${
                    isActive
                      ? 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                >
                  {isActive ? <FolderOpen size={14} className="shrink-0" /> : <Folder size={14} className="shrink-0" />}
                  <span className="truncate flex-1">{folder.name}</span>
                  <span className="text-[10px] opacity-70">{count}</span>
                </button>
              )}
              {!isEditing && canManageFolders && canWriteLibrary && (
                <div className="absolute right-1 top-1/2 -translate-y-1/2 hidden group-hover:flex gap-0.5">
                  <button
                    type="button"
                    className="p-0.5 rounded text-slate-400 hover:text-blue-600"
                    title={t('sectionPresetFolderRename', { defaultValue: 'Renomear' })}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingFolderId(folder.id);
                      setEditingFolderName(folder.name || '');
                    }}
                  >
                    <Pencil size={11} />
                  </button>
                  <button
                    type="button"
                    className="p-0.5 rounded text-slate-400 hover:text-red-600"
                    title={t('sectionPresetFolderDelete', { defaultValue: 'Excluir pasta' })}
                    disabled={folderBusyId === folder.id}
                    onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder); }}
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="p-2 border-t border-slate-200 dark:border-slate-700">
        {canManageFolders && canWriteLibrary && (showNewFolderInput ? (
          <div className="space-y-1.5">
            <input
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder={t('sectionPresetFolderNamePlaceholder', { defaultValue: 'Nome da pasta' })}
              className="w-full px-2 py-1.5 text-xs border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateFolder();
                if (e.key === 'Escape') { setShowNewFolderInput(false); setNewFolderName(''); }
              }}
            />
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => { setShowNewFolderInput(false); setNewFolderName(''); }}
                className="flex-1 py-1 text-[10px] font-semibold rounded border border-slate-200 dark:border-slate-600"
              >
                {t('cancel', { defaultValue: 'Cancelar' })}
              </button>
              <button
                type="button"
                onClick={handleCreateFolder}
                disabled={creatingFolder || !newFolderName.trim()}
                className="flex-1 py-1 text-[10px] font-semibold rounded bg-violet-600 text-white disabled:opacity-50"
              >
                {creatingFolder ? '…' : t('create', { defaultValue: 'Criar' })}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowNewFolderInput(true)}
            className="w-full flex items-center justify-center gap-1 py-1.5 text-[11px] font-semibold text-violet-700 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-900/20 rounded-lg border border-dashed border-violet-300 dark:border-violet-600"
          >
            <Plus size={12} />
            {t('sectionPresetNewFolder', { defaultValue: 'Nova pasta' })}
          </button>
        ))}
      </div>
    </aside>
  );

  const renderSectionList = (emptyMessage) => (
    <div className="flex-1 overflow-y-auto p-2 min-h-[180px]">
      {loading ? (
        <div className="flex items-center justify-center py-12 text-slate-400">
          <Loader2 className="animate-spin mr-2" size={20} />
          {t('loading', { defaultValue: 'Carregando…' })}
        </div>
      ) : loadError ? (
        <div className="text-center text-red-600 dark:text-red-400 text-sm py-8 px-4 space-y-2">
          <p>{loadError}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {t('sectionPresetListErrorHint', {
              defaultValue:
                'Confira se o backend foi atualizado e reiniciado (PM2). Se persistir, execute doc/sql/section-presets-table.sql no banco.',
            })}
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-center text-slate-400 text-sm py-8">{emptyMessage}</p>
      ) : (
        <ul className="space-y-1">
          {filtered.map((s) => (
            <li key={s.id}>
              <div className={`w-full px-3 py-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 border transition-colors flex items-start gap-2 ${
                editingAiId === s.id ? 'border-violet-300 dark:border-violet-700 bg-violet-50/50 dark:bg-violet-950/20' : 'border-transparent hover:border-slate-200 dark:hover:border-slate-700'
              }`}
              >
                {mode === 'insert' ? (
                  <button
                    type="button"
                    onClick={() => handleInsert(s)}
                    disabled={insertingId === s.id}
                    className="flex flex-1 min-w-0 items-start gap-3 text-left bg-transparent border-none p-0 cursor-pointer"
                  >
                    <LayoutList size={18} className={`shrink-0 mt-0.5 ${isAiLibrary ? 'text-violet-500' : 'text-violet-500'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-slate-800 dark:text-slate-100 truncate">{s.name}</div>
                      {s.description ? (
                        <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 mt-0.5">{s.description}</p>
                      ) : null}
                      <p className="text-[10px] text-slate-400 mt-1">
                        {isAiLibrary ? (
                          <>
                            {s.section_role ? <span className="text-violet-500 font-semibold">{s.section_role}</span> : null}
                            {s.section_role && s.status ? ' · ' : ''}
                            {s.status ? <span>{s.status}</span> : null}
                            {(s.section_role || s.status) ? ' · ' : ''}
                            {parseSectionTags(s.tags).slice(0, 4).join(', ')}
                            {(s.section_role || s.status || parseSectionTags(s.tags).length) ? ' · ' : ''}
                          </>
                        ) : (
                          <>
                            {activeFolderId === FOLDER_ALL && s.folder_id ? (
                              <span className="text-violet-500">{folderLabelById(s.folder_id, folders, t, noFolderLabel)} · </span>
                            ) : null}
                            {s.author_name ? `${s.author_name} · ` : ''}
                          </>
                        )}
                        {formatSectionDate(s.updated_at || s.created_at, locale)}
                      </p>
                    </div>
                    {insertingId === s.id && <Loader2 size={16} className="animate-spin text-blue-500 shrink-0" />}
                  </button>
                ) : (
                  <div className="flex flex-1 min-w-0 items-start gap-3">
                    <BookmarkPlus size={18} className={`shrink-0 mt-0.5 ${isAiLibrary ? 'text-violet-500' : 'text-blue-500'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-slate-800 dark:text-slate-100 truncate">{s.name}</div>
                      {s.description ? (
                        <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1 mt-0.5">{s.description}</p>
                      ) : null}
                      {isAiLibrary && (
                        <p className="text-[10px] text-violet-600 dark:text-violet-300 mt-0.5">
                          {[s.section_role, s.status, s.tier_min].filter(Boolean).join(' · ')}
                          {parseSectionTags(s.tags).length ? ` · ${parseSectionTags(s.tags).slice(0, 3).join(', ')}` : ''}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {isAiLibrary && canDelete && (
                  <button
                    type="button"
                    onClick={(e) => openAiMetaEditor(s, e)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20 shrink-0"
                    title={t('sectionPresetAiEditMeta', { defaultValue: 'Editar metadados' })}
                  >
                    <Pencil size={14} />
                  </button>
                )}
                {!isAiLibrary && canWriteLibrary && (
                <select
                  className="text-[10px] max-w-[96px] shrink-0 px-1 py-1 border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
                  value={s.folder_id || ''}
                  title={t('sectionPresetMoveToFolder', { defaultValue: 'Mover para pasta' })}
                  onChange={(e) => handleMoveSection(s, e.target.value || null)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <option value="">{noFolderLabel}</option>
                  {folders.map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
                )}
                {canDelete && (isAiLibrary || canWriteLibrary) && (
                  <button
                    type="button"
                    onClick={(e) => handleDelete(s, e)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 shrink-0"
                    title={t('sectionPresetDelete', { defaultValue: 'Excluir' })}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  if (!open) return null;

  const title =
    mode === 'save'
      ? t('sectionPresetSaveTitle', { defaultValue: 'Salvar seção na biblioteca' })
      : t('sectionPresetInsertTitle', { defaultValue: 'Inserir seção da biblioteca' });

  return createPortal(
    <div
      className="fixed inset-0 z-[100030] flex items-center justify-center bg-black/55 p-3 md:p-6"
      role="dialog"
      aria-modal="true"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="flex flex-col w-full max-w-3xl max-h-[min(90vh,760px)] rounded-2xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 shrink-0 gap-3">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2 min-w-0">
            {mode === 'save'
              ? <BookmarkPlus size={22} className={isAiLibrary ? 'text-violet-600 shrink-0' : 'text-blue-600 shrink-0'} />
              : <LayoutList size={22} className="text-violet-600 shrink-0" />}
            <span className="truncate">
              {isAiLibrary
                ? (mode === 'save'
                  ? t('sectionPresetSaveAiTitle', { defaultValue: 'Salvar na biblioteca IA' })
                  : t('sectionPresetAiLibraryTitle', { defaultValue: 'Biblioteca IA (Global)' }))
                : title}
            </span>
          </h2>
          <div className="flex items-center gap-2 shrink-0">
            {canCurateAiLibrary && (
              <div className="flex rounded-lg border border-slate-200 dark:border-slate-600 overflow-hidden text-[11px] font-semibold">
                <button
                  type="button"
                  onClick={() => switchLibraryScope('tenant')}
                  className={`px-2 py-1.5 ${!isAiLibrary ? 'bg-blue-600 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300'}`}
                >
                  {t('sectionPresetSaveTargetTenant', { defaultValue: 'Equipe' })}
                </button>
                <button
                  type="button"
                  onClick={() => switchLibraryScope('ai_global')}
                  className={`px-2 py-1.5 ${isAiLibrary ? 'bg-violet-600 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300'}`}
                >
                  {t('sectionPresetSaveTargetAi', { defaultValue: 'IA Global' })}
                </button>
              </div>
            )}
            <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500" aria-label={t('close', { defaultValue: 'Fechar' })}>
              <X size={22} />
            </button>
          </div>
        </div>

        <div className="flex flex-1 min-h-0">
          {!isAiLibrary && renderFolderSidebar()}

          {mode === 'save' ? (
            <div className="flex-1 flex flex-col min-w-0 min-h-0">
              <div className="p-4 space-y-3 overflow-y-auto border-b border-slate-100 dark:border-slate-800">
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {saveToAi
                    ? t('sectionPresetSaveAiHint', {
                        defaultValue: 'Salva na biblioteca Global da IA (somente curadoria Mestra). Não aparece na biblioteca da equipe dos clientes.',
                      })
                    : t('sectionPresetSaveHint', {
                        defaultValue: 'A seção será salva na biblioteca compartilhada da equipe — visível em todos os projetos do cliente e para colaboradores com permissão de edição.',
                      })}
                </p>
                {editingAiId && (
                  <div className="rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50/80 dark:bg-violet-950/30 p-3 space-y-2">
                    <p className="text-xs font-semibold text-violet-800 dark:text-violet-200">
                      {t('sectionPresetAiEditingMeta', { defaultValue: 'Editando metadados (sem alterar o layout da seção)' })}
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={saveAiMeta}
                        disabled={aiMetaSaving}
                        className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-violet-600 text-white disabled:opacity-50"
                      >
                        {aiMetaSaving ? '…' : t('mediaSaveMeta', { defaultValue: 'Salvar' })}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingAiId(null)}
                        className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 dark:border-slate-600"
                      >
                        {t('cancel', { defaultValue: 'Cancelar' })}
                      </button>
                    </div>
                  </div>
                )}
                {!saveToAi && (
                <>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                    {t('sectionPresetSaveFolderLabel', { defaultValue: 'Salvar na pasta' })}
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800"
                    value={saveFolderId}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSaveFolderId(v);
                      setOverwriteId('');
                      setActiveFolderId(v || FOLDER_NONE);
                    }}
                  >
                    <option value="">{noFolderLabel}</option>
                    {folders.map((f) => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>
                {overwriteOptions.length > 0 && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                      {t('sectionPresetOverwriteLabel', { defaultValue: 'Atualizar seção existente (opcional)' })}
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800"
                      value={overwriteId}
                      onChange={(e) => handleOverwriteChange(e.target.value)}
                    >
                      <option value="">{t('sectionPresetNewOption', { defaultValue: '— Nova seção —' })}</option>
                      {overwriteOptions.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                </>
                )}
                {saveToAi && overwriteOptions.length > 0 && !editingAiId && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                      {t('sectionPresetAiOverwriteLabel', { defaultValue: 'Atualizar seção Global existente (opcional)' })}
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800"
                      value={overwriteId}
                      onChange={(e) => handleOverwriteChange(e.target.value)}
                    >
                      <option value="">{t('sectionPresetNewOption', { defaultValue: '— Nova seção —' })}</option>
                      {overwriteOptions.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                    {t('sectionPresetNameLabel', { defaultValue: 'Nome da seção' })} *
                  </label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800"
                    placeholder={t('sectionPresetNamePlaceholder', { defaultValue: 'Ex: Hero com vídeo lateral' })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                    {t('descriptionOptional', { defaultValue: 'Descrição (opcional)' })}
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800"
                  />
                </div>
                {saveToAi && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1 border-t border-slate-100 dark:border-slate-800">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                        {t('sectionPresetAiRole', { defaultValue: 'Papel da seção' })}
                      </label>
                      <select
                        value={aiSectionRole}
                        onChange={(e) => setAiSectionRole(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800"
                      >
                        <option value="hero">hero</option>
                        <option value="content">content</option>
                        <option value="gallery">gallery</option>
                        <option value="cta">cta</option>
                        <option value="quiz">quiz</option>
                        <option value="footer">footer</option>
                        <option value="other">other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                        {t('sectionPresetAiTierMin', { defaultValue: 'Tier mínimo' })}
                      </label>
                      <select
                        value={aiTierMin}
                        onChange={(e) => setAiTierMin(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800"
                      >
                        <option value="basic">basic</option>
                        <option value="expert">expert</option>
                        <option value="premium">premium</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                        {t('sectionPresetAiMood', { defaultValue: 'Tom / mood' })}
                      </label>
                      <input
                        value={aiMood}
                        onChange={(e) => setAiMood(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800"
                        placeholder="ex: corporativo, acolhedor"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                        {t('sectionPresetAiStatus', { defaultValue: 'Status' })}
                      </label>
                      <select
                        value={aiStatus}
                        onChange={(e) => setAiStatus(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800"
                      >
                        <option value="draft">draft</option>
                        <option value="ready">ready</option>
                        <option value="archived">archived</option>
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                        {t('sectionPresetAiTags', { defaultValue: 'Tags (separadas por vírgula)' })}
                      </label>
                      <input
                        value={aiTags}
                        onChange={(e) => setAiTags(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800"
                        placeholder="ex: video, 2colunas, dark"
                      />
                    </div>
                  </div>
                )}
                <div className="flex gap-3 pt-1">
                  <button type="button" onClick={onClose} className="flex-1 py-2.5 text-slate-600 font-semibold rounded-lg border border-slate-200 dark:border-slate-600">
                    {t('cancel', { defaultValue: 'Cancelar' })}
                  </button>
                  {editingAiId ? (
                    <button
                      type="button"
                      onClick={saveAiMeta}
                      disabled={aiMetaSaving}
                      className="flex-1 py-2.5 bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {aiMetaSaving ? <Loader2 size={16} className="animate-spin" /> : <Pencil size={16} />}
                      {t('sectionPresetAiSaveMetaBtn', { defaultValue: 'Salvar metadados' })}
                    </button>
                  ) : (
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className={`flex-1 py-2.5 text-white font-semibold rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 ${
                      saveToAi ? 'bg-violet-600 hover:bg-violet-700' : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <BookmarkPlus size={16} />}
                    {saving
                      ? t('submitting', { defaultValue: 'Enviando…' })
                      : saveToAi
                        ? t('sectionPresetSaveAiBtn', { defaultValue: 'Salvar na biblioteca IA' })
                        : t('sectionPresetSaveBtn', { defaultValue: 'Salvar seção' })}
                  </button>
                  )}
                </div>
              </div>
              <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-800">
                <p className="text-[11px] font-semibold text-slate-500 mb-1">
                  {isAiLibrary
                    ? t('sectionPresetAiBrowseList', { defaultValue: 'Seções Globais salvas' })
                    : t('sectionPresetBrowseInFolder', { defaultValue: 'Seções nesta pasta' })}
                </p>
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={t('sectionPresetSearchPlaceholder', { defaultValue: 'Buscar seção…' })}
                    className="w-full pl-8 pr-3 py-1.5 border border-slate-200 dark:border-slate-600 rounded-lg text-xs bg-white dark:bg-slate-800"
                  />
                </div>
              </div>
              {renderSectionList(
                isAiLibrary
                  ? t('sectionPresetAiEmpty', { defaultValue: 'Nenhuma seção Global ainda. Salve uma com a aba IA Global.' })
                  : t('sectionPresetEmptyInFolder', { defaultValue: 'Nenhuma seção nesta pasta ainda.' })
              )}
            </div>
          ) : (
            <div className="flex-1 flex flex-col min-w-0 min-h-0">
              <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-800 shrink-0">
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                  {isAiLibrary
                    ? t('sectionPresetAiInsertHint', {
                        defaultValue: 'Biblioteca Global da IA. Clique para inserir no palco e validar o layout; use o lápis para editar metadados ou a lixeira para excluir.',
                      })
                    : t('sectionPresetInsertHint', {
                        defaultValue: 'Biblioteca compartilhada entre todos os projetos do cliente. A seção será inserida após a linha selecionada no palco.',
                      })}
                </p>
                {editingAiId && isAiLibrary && (
                  <div className="mb-3 rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50/80 dark:bg-violet-950/30 p-3 space-y-2">
                    <p className="text-xs font-semibold text-violet-800 dark:text-violet-200">
                      {t('sectionPresetAiEditingMeta', { defaultValue: 'Editando metadados' })}
                    </p>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-2 py-1.5 border border-slate-200 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-800"
                      placeholder="Nome"
                    />
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={2}
                      className="w-full px-2 py-1.5 border border-slate-200 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-800"
                      placeholder="Descrição"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <select value={aiSectionRole} onChange={(e) => setAiSectionRole(e.target.value)} className="px-2 py-1.5 border rounded text-xs bg-white dark:bg-slate-800 dark:border-slate-600">
                        {['hero', 'content', 'gallery', 'cta', 'quiz', 'footer', 'other'].map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <select value={aiStatus} onChange={(e) => setAiStatus(e.target.value)} className="px-2 py-1.5 border rounded text-xs bg-white dark:bg-slate-800 dark:border-slate-600">
                        <option value="draft">draft</option>
                        <option value="ready">ready</option>
                        <option value="archived">archived</option>
                      </select>
                      <select value={aiTierMin} onChange={(e) => setAiTierMin(e.target.value)} className="px-2 py-1.5 border rounded text-xs bg-white dark:bg-slate-800 dark:border-slate-600">
                        <option value="basic">basic</option>
                        <option value="expert">expert</option>
                        <option value="premium">premium</option>
                      </select>
                      <input value={aiMood} onChange={(e) => setAiMood(e.target.value)} className="px-2 py-1.5 border rounded text-xs bg-white dark:bg-slate-800 dark:border-slate-600" placeholder="mood" />
                    </div>
                    <input
                      value={aiTags}
                      onChange={(e) => setAiTags(e.target.value)}
                      className="w-full px-2 py-1.5 border rounded text-xs bg-white dark:bg-slate-800 dark:border-slate-600"
                      placeholder="tags"
                    />
                    <div className="flex gap-2">
                      <button type="button" onClick={saveAiMeta} disabled={aiMetaSaving} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-violet-600 text-white disabled:opacity-50">
                        {aiMetaSaving ? '…' : t('mediaSaveMeta', { defaultValue: 'Salvar' })}
                      </button>
                      <button type="button" onClick={() => setEditingAiId(null)} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 dark:border-slate-600">
                        {t('cancel', { defaultValue: 'Cancelar' })}
                      </button>
                    </div>
                  </div>
                )}
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={t('sectionPresetSearchPlaceholder', { defaultValue: 'Buscar seção…' })}
                    className="w-full pl-9 pr-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800"
                  />
                </div>
              </div>
              {renderSectionList(
                isAiLibrary
                  ? t('sectionPresetAiEmpty', { defaultValue: 'Nenhuma seção Global ainda.' })
                  : t('sectionPresetEmpty', { defaultValue: 'Nenhuma seção salva pela equipe ainda.' })
              )}
              <div className="p-4 border-t border-slate-200 dark:border-slate-700 shrink-0">
                <button type="button" onClick={onClose} className="w-full py-2.5 text-slate-600 font-semibold rounded-lg border border-slate-200 dark:border-slate-600">
                  {t('cancel', { defaultValue: 'Cancelar' })}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
